import { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'

import { DeliverySlot, DeliverySlotData } from 'modules/delivery'
import { Collapse } from 'shared/ui/Collapse'
import type { CollapseProps } from 'shared/ui/Collapse/Collapse'
import { RadioGroup, RadioItem } from 'shared/ui/RadioGroup'
import type { RadioGroupProps } from 'shared/ui/RadioGroup/RadioGroup'
import type { RadioItemProps } from 'shared/ui/RadioGroup/RadioItem'
import {
  replaceDateDashToSnakeCase,
  replaceDateSnakeCaseToDash
} from 'shared/utils/date'

import { CartDeliveryFreeSlotsBadge } from './CartDeliveryFreeSlotsBadge'
import CartDeliverySlot from './CartDeliverySlot'

import styles from './CartDeliveryDays.module.scss'

interface CartDeliveryDaysProps {
  data: DeliverySlotData
  selectedSlot: DeliverySlot | null
  setSelectedSlot: (slot: DeliverySlot | null) => void
  showFreeBadge: boolean
}

const CartDeliveryDays = ({
  data,
  selectedSlot,
  setSelectedSlot,
  showFreeBadge
}: CartDeliveryDaysProps) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  useEffect(() => {
    setSelectedDate(null)
  }, [data])
  useEffect(() => {
    const selectedSlotDate = selectedSlot?.date || null

    setSelectedDate(selectedSlotDate)
  }, [selectedSlot])

  const slots = useMemo(() => {
    if (!selectedDate) return []

    return data[replaceDateDashToSnakeCase(selectedDate)] || []
  }, [data, selectedDate])

  const days = useMemo(() => {
    return Object.entries(data).map(([dateSnakeCase, availableSlots]) => {
      const date = replaceDateSnakeCaseToDash(dateSnakeCase)
      const hasFreeDeliverySlots = availableSlots.some(
        slot => slot.isFreeForCart
      )

      return { date, hasFreeDeliverySlots }
    })
  }, [data])

  const handleSelectDate = (date: string) => {
    setSelectedDate(date)
  }

  const handleSelectSlot = useCallback(
    (slotId: string) => {
      setSelectedSlot(slots.find(slot => slot.id === slotId) || null)
    },
    [slots, setSelectedSlot]
  )

  return (
    <div className={styles.wrapper}>
      <DaysWrapper
        name='delivery-days'
        selectedValue={selectedDate}
        onChange={handleSelectDate}
        orientation='horizontal'
        hasRadioMark={false}
        variant='boxed'
      >
        {days.map(day => {
          const date = new Date(day.date)
          const [weekday, month] = date
            .toLocaleDateString('Pl-pl', {
              month: 'short',
              weekday: 'long'
            })
            .split(' ')

          return (
            <DayBox
              key={day.date}
              id={day.date}
              value={day.date}
              $isSelected={selectedDate === day.date}
            >
              {day.hasFreeDeliverySlots && showFreeBadge && (
                <CartDeliveryFreeSlotsBadge />
              )}
              <b>{`${date.getDate()} ${weekday}`}</b>
              <span>{month}</span>
            </DayBox>
          )
        })}
      </DaysWrapper>
      <DeliverySlotsWrapper
        open={!!selectedDate && slots.length > 0}
        transitionDuration={150}
      >
        {selectedDate && (
          <>
            <SlotsColumn>
              <DeliverySlotsList
                name='delivery-slots'
                selectedValue={selectedSlot?.id || null}
                onChange={handleSelectSlot}
                orientation='vertical'
              >
                {slots.map(slot => {
                  return (
                    <CartDeliverySlot
                      key={slot.id}
                      value={slot.id}
                      label={`${slot.start}-${slot.end}`}
                      isFreeForCart={slot.isFreeForCart}
                      disabled={!slot.isAvailable}
                    />
                  )
                })}
              </DeliverySlotsList>
            </SlotsColumn>
          </>
        )}
      </DeliverySlotsWrapper>
    </div>
  )
}

export default CartDeliveryDays

const DaysWrapper = ({ className, ...props }: RadioGroupProps) => (
  <RadioGroup className={clsx(styles.daysWrapper, className)} {...props} />
)

const DayBox = ({
  $isSelected,
  className,
  ...props
}: RadioItemProps & { $isSelected: boolean }) => (
  <RadioItem
    className={clsx(
      styles.dayBox,
      { [styles.dayBoxUnselected]: !$isSelected },
      className
    )}
    {...props}
  />
)

const DeliverySlotsWrapper = ({ className, ...props }: CollapseProps) => (
  <Collapse
    className={clsx(styles.deliverySlotsWrapper, className)}
    {...props}
  />
)

const SlotsColumn = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx(styles.slotsColumn, className)} {...props} />
)

const DeliverySlotsList = ({ className, ...props }: RadioGroupProps) => (
  <RadioGroup className={clsx(styles.deliverySlotsList, className)} {...props} />
)
